'use client';

import React, { useEffect, useRef } from 'react';

const MarketingServicesSection = () => {
    const pixelContainerRef = useRef<HTMLDivElement>(null);
    const [pixelCount, setPixelCount] = React.useState(0);

    useEffect(() => {
        const isMobile = window.innerWidth < 768;
        const isTablet = window.innerWidth < 1024;
        setPixelCount(isMobile ? 96 : isTablet ? 192 : 400);
    }, []);

    useEffect(() => {
        if (pixelCount === 0) return;

        let timeoutId: NodeJS.Timeout;
        const init = () => {
            const w = window as any;
            if (!w.gsap || !w.ScrollTrigger) {
                timeoutId = setTimeout(init, 100);
                return;
            }
            const gsap = w.gsap;

            const container = pixelContainerRef.current;
            if (!container) {
                timeoutId = setTimeout(init, 100);
                return;
            }

            const items = container.querySelectorAll(".pixel_item");
            if (items.length === 0) {
                timeoutId = setTimeout(init, 100);
                return;
            }
            const itemsArray = Array.from(items);

            // Sort items randomly for the stagger effect
            const sortedItems = itemsArray.map((e, index) => ({
                pixel: e,
                sort: Math.random() + index / itemsArray.length
            })).sort((a, b) => b.sort - a.sort).map(e => e.pixel);

            // Initial state: Fully visible (blocking the view)
            gsap.set(items, { opacity: 1 });

            const isMobile = window.innerWidth < 768;
            const isTablet = window.innerWidth < 1024;
            const cols = isMobile ? 8 : isTablet ? 12 : 16;
            const rows = isMobile ? 12 : isTablet ? 16 : 25;

            // Animate to invisible (reveal underlying content)
            gsap.to(sortedItems, {
                opacity: 0,
                stagger: {
                    amount: 1.0,
                    grid: [cols, rows],
                    axis: "y"
                },
                ease: "power2.inOut",
                scrollTrigger: {
                    trigger: container.parentElement, // Trigger based on the section
                    scrub: true,
                    start: isMobile ? "top 70%" : "top bottom", // 80 default Start fading when section enters viewport
                    end: isMobile ? "top 10%" : "center center", // Finish fading when section is in middle
                    invalidateOnRefresh: true
                }
            });
        };

        init();

        return () => clearTimeout(timeoutId);
    }, [pixelCount]);

    const services = [
        {
            id: '02',
            title: 'Performance',
            description: 'Core Web Vitals optimization for lightning-fast load times and smooth interactions.',
            theme: 'light'
        },
        {
            id: '03',
            title: 'SEO Ready',
            description: 'Semantic HTML and Server-Side Rendering for maximum search engine visibility.',
            theme: 'light'
        },
        {
            id: '04',
            title: 'Security',
            description: 'Implementation of SSL, firewalls, and best practices to keep your data safe.',
            theme: 'light'
        },
        {
            id: '05',
            title: 'Cloud Infra',
            description: 'Scalable cloud infrastructure setup using AWS, Vercel, or modern equivalents.',
            theme: 'light'
        }
    ];

    return (
        <section className="marketing-services-section" data-header-theme="dark">
            <style dangerouslySetInnerHTML={{
                __html: `
                .marketing-services-section {
                    background-color: #fff;
                    color: #000;
                    padding: 250px 0 100px 0;
                    position: relative;
                    z-index: 10;
                    overflow: hidden; /* Ensure pixels don't overflow */
                }
                @media (max-width: 768px) {
                    .marketing-services-section {
                        padding: 100px 0 50px 0;
                    }
                }
                .pixel_transition_overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    display: grid;
                    grid-template-columns: repeat(16, 1fr);
                    /* Rows will stretch to fill height */
                    z-index: 20;
                    pointer-events: none; /* Allow interaction through pixels */
                }
                @media (max-width: 1024px) {
                    .pixel_transition_overlay {
                        grid-template-columns: repeat(12, 1fr);
                    }
                }
                @media (max-width: 768px) {
                    .pixel_transition_overlay {
                        grid-template-columns: repeat(8, 1fr);
                    }
                }
                .pixel_item {
                    width: 100%;
                    aspect-ratio: 1; /* Perfect Square always */
                    background-color: #EAEAEA;
                    min-height: 1px; 
                }
                .pixel_item.pixel_black {
                    background-color: #070707;
                }
                /* ... existing styles ... */
                .marketing-container {
                    width: 100%;
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 0 20px;
                }
                .marketing-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 60px;
                    flex-wrap: wrap;
                }
                .marketing-subheading {
                    font-size: 14px;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    font-weight: 600;
                    margin-top: 10px;
                }
                .marketing-heading {
                    font-size: clamp(3rem, 5vw, 4.5rem);
                    line-height: 1;
                    font-weight: 500;
                    text-align: right;
                    max-width: 600px;
                    color: #000;
                }
                .marketing-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 20px;
                    width: 100%;
                }
                .marketing-card {
                    padding: 40px;
                    border-radius: 30px;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    min-height: 300px;
                }
                .card-dark {
                    background-color: #070707;
                    color: #fff;
                    grid-row: span 2;
                }
                .card-light {
                    background-color: #f2f4f7;
                    color: #000;
                }
                .marketing-card-header {
                    margin-bottom: 20px;
                }
                .marketing-card-id {
                    font-size: 16px;
                    opacity: 0.5;
                    margin-bottom: 10px;
                }
                .marketing-card-title {
                    font-size: 28px;
                    font-weight: 500;
                    line-height: 1.2;
                }
                .marketing-card-description {
                    font-size: 18px;
                    line-height: 1.5;
                    opacity: 0.7;
                    margin-top: auto;
                }
                .card-dark .marketing-card-id, .card-dark .marketing-card-description {
                    color: #999;
                    opacity: 1;
                }
                
                @media (max-width: 1024px) {
                    .marketing-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                    .card-dark {
                        grid-column: span 2;
                        grid-row: span 1;
                        min-height: 400px;
                    }
                    .marketing-heading {
                        text-align: left;
                        margin-top: 20px;
                    }
                }

                @media (max-width: 768px) {
                    .marketing-grid {
                        grid-template-columns: 1fr;
                    }
                    .card-dark {
                        grid-column: span 1;
                    }
                    .marketing-heading {
                        font-size: 2.5rem;
                    }
                    .marketing-header {
                        flex-direction: column;
                    }
                }
            ` }} />

            {/* Pixel Overlay */}
            <div className="pixel_transition_overlay" ref={pixelContainerRef}>
                {Array.from({ length: pixelCount }).map((_, i) => (
                    <div key={i} className="pixel_item pixel_black"></div>
                ))}
            </div>

            <div className="marketing-container">
                <div className="marketing-header">
                    <div className="marketing-subheading">Technical Excellence</div>
                    <h2 className="marketing-heading">Modern Web Standards</h2>
                </div>

                <div className="marketing-grid">
                    <div className="marketing-card card-dark">
                        <div className="marketing-card-header">
                            <div className="marketing-card-id">01</div>
                            <h3 className="marketing-card-title">Full-Stack Architecture</h3>
                        </div>
                        <p className="marketing-card-description">
                            Building robust, scalable applications with modern frameworks like Next.js, React, and Node.js.
                        </p>
                        <div style={{ flexGrow: 1 }}></div>
                    </div>

                    {services.map((service) => (
                        <div key={service.id} className="marketing-card card-light">
                            <div className="marketing-card-header">
                                <div className="marketing-card-id">{service.id}</div>
                                <h3 className="marketing-card-title">{service.title}</h3>
                            </div>
                            <p className="marketing-card-description">{service.description}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default MarketingServicesSection;
